import { create } from 'zustand';
import { useGameStore } from './gameStore';
import type { DialoguePool, DialogueTrigger } from './types';

/**
 * Selects a random index from the pool, ensuring it doesn't repeat the last one
 * if the pool has more than one line.
 */
export function nextRandomIndex(
  trigger: DialogueTrigger,
  lastIdx: number,
  poolSize: number,
): number {
  if (poolSize <= 1) return 0;

  let nextIdx = Math.floor(Math.random() * poolSize);
  // Avoid immediate repetition
  if (nextIdx === lastIdx) {
    nextIdx = (nextIdx + 1) % poolSize;
  }
  return nextIdx;
}

export const INITIAL_DIALOGUE_INDEX: Record<DialogueTrigger, number> = {
  slowPlay: -1,
  tokenLoss: -1,
  solution: -1,
  spite: -1,
};

export interface DialogueState {
  currentLine: string | null;
  currentPool: DialoguePool | null;
  lastIndex: Record<DialogueTrigger, number>;
  /** When true, the DialogueBox arrow invites the player to commit a
   * queued level advance (see `LevelController.solve`). Transient. */
  pendingAdvance: boolean;
  setPool: (pool: DialoguePool | null) => void;
  setLine: (line: string) => void;
  fire: (trigger: DialogueTrigger) => void;
  clearLine: () => void;
  setPendingAdvance: (v: boolean) => void;
  /** User acknowledges the queued solution line; commits the level advance
   * and clears the gate. No-op if nothing is pending. */
  confirmAdvance: () => void;
  _devReset: () => void;
}

export const useDialogueStore = create<DialogueState>((set, get) => ({
  currentLine: null,
  currentPool: null,
  lastIndex: { ...INITIAL_DIALOGUE_INDEX },
  pendingAdvance: false,

  setPool: (pool) => {
    // Navigating between levels drops any pending gate — the new level
    // starts from a clean slate.
    // We do NOT reset lastIndex here anymore, to maintain variety across
    // level transitions where possible (though each level has a new pool).
    set({
      currentPool: pool,
      pendingAdvance: false,
    });
  },

  setLine: (line) => {
    set({ currentLine: line });
  },

  fire: (trigger) => {
    const { currentPool, lastIndex } = get();
    if (!currentPool) return;

    const poolForTrigger = currentPool[trigger];
    const poolSize = poolForTrigger.length;

    const nextIdx = nextRandomIndex(trigger, lastIndex[trigger], poolSize);
    const line = poolForTrigger[nextIdx];

    if (typeof line !== 'string') return;

    set({
      currentLine: line,
      lastIndex: { ...lastIndex, [trigger]: nextIdx },
    });
  },

  clearLine: () => {
    set({ currentLine: null });
  },

  setPendingAdvance: (v) => {
    set({ pendingAdvance: v });
  },

  confirmAdvance: () => {
    if (!get().pendingAdvance) return;
    set({ pendingAdvance: false });
    useGameStore.getState().solveLevel();
  },

  _devReset: () => {
    set({
      currentLine: null,
      currentPool: null,
      lastIndex: { ...INITIAL_DIALOGUE_INDEX },
      pendingAdvance: false,
    });
  },
}));
